// Support centre + boards (VRG-226) react-query hooks.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supportApi } from 'services'
import { ROUTES } from 'routes'
import type {
  BoardItemStatus,
  SupportRequestStatus,
  SupportRequestType,
  SupportQueueScope,
} from 'services/supportApi'

const REQUESTS_KEY = ['support', 'requests']
// BOARD_KEY was here — the query key for the customer feature board. Gone with the
// board (VRG-324): no query reads it, so every `invalidateQueries` against it was a
// no-op that read like a cache concern.
const INTERNAL_KEY = ['internal', 'support']

export const useSupportPermissions = () =>
  useQuery({
    queryKey: ['support', 'permissions'],
    queryFn: () => supportApi.permissions(),
    // Permissions change with a role edit, not minute to minute; a long stale
    // time keeps every gated affordance from refetching on each mount.
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

/**
 * Where a "go and look at your requests" link should send THIS caller (VRG-324).
 *
 * One rule, two call sites — the retired `/support/requests` redirect and the
 * What's-new "you asked for this" links — and it is a rule that is easy to get
 * subtly wrong in a way nothing catches: send a member without
 * `View company requests` to the company list and they land on a section their own
 * nav renders disabled, behind an API that 403s. That is not better than the 404
 * the redirect was introduced to avoid.
 *
 * `null` while permissions are still loading, so a caller can render nothing
 * rather than navigate to a guess and then correct itself.
 */
export const useSupportListRoute = (): string | null => {
  const { data, isLoading } = useSupportPermissions()
  if (isLoading) return null
  return data?.canViewCompanyRequests
    ? ROUTES.SETTINGS_COMPANY_SUPPORT
    : ROUTES.SETTINGS_PERSONAL_SUPPORT
}

export const useSupportRequests = (scope: 'mine' | 'company' = 'mine') =>
  useQuery({
    queryKey: [...REQUESTS_KEY, scope],
    queryFn: () => supportApi.list(scope),
    retry: false,
  })

export const useSupportRequest = (id: string | undefined) =>
  useQuery({
    queryKey: [...REQUESTS_KEY, 'detail', id],
    queryFn: () => supportApi.detail(id!),
    enabled: Boolean(id),
    retry: false,
  })

export const useCreateSupportRequest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      type: SupportRequestType
      /** VRG-266 — what it is about. Set only by an intake that knows; omitted otherwise. */
      topic?: 'custom_tag' | null
      body: string
      attachmentUrls?: string[] | null
      pageUrl?: string | null
      linkToRequestId?: string | null
    }) => supportApi.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
  })
}

/**
 * "Has someone already asked for this?", as the user types (VRG-324).
 *
 * `enabled` on a minimum length rather than firing from the first keystroke: the
 * API returns nothing for a query with no ≥3-character non-stopword token, so
 * shorter calls are guaranteed-empty round trips on every keystroke.
 *
 * `staleTime` is generous because the answer barely moves — a colleague filing a
 * duplicate DURING your typing is not a case worth a refetch — and `placeholderData`
 * keeps the previous suggestions on screen while the next query resolves, so the
 * panel does not blink out and back between keystrokes.
 */
export const useSimilarRequests = (query: string, enabled = true) => {
  const trimmed = query.trim()
  return useQuery({
    queryKey: ['support', 'similar', trimmed],
    queryFn: () => supportApi.searchSimilar(trimmed),
    enabled: enabled && trimmed.length >= 6,
    staleTime: 60 * 1000,
    placeholderData: previous => previous,
    retry: false,
  })
}

export const useReplyToSupportRequest = (id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { body: string; attachmentUrls?: string[] | null }) =>
      supportApi.reply(id, payload),
    // Invalidate the whole request tree, not just the thread: a customer reply
    // can reopen a resolved request server-side, so the LIST's status is stale too.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
  })
}

/*
  `useBoardItems` and `useToggleBoardVote` were here. Removed with the customer
  feature board (VRG-324) — nothing rendered them once FeatureRequestsBoardPage was
  deleted, and a vote hook with no caller is a vote affordance waiting to be
  re-added by someone who does not know why the board went away.

  Their ENDPOINTS (`GET /support/board`, `POST /support/board/:id/vote`) are left in
  place deliberately. They are tenant-safe, unreachable from any UI, and
  VRG-249-Boards.Converge.Integrations.Onto.BoardItems is the change that should
  decide their fate — removing routes an unrelated batch does not own is how you
  break a consumer nobody checked for.
*/

// ── Internal (Vera staff) ──────────────────────────────────────────────────

export const useInternalSupportRequests = (
  status?: SupportRequestStatus,
  scope: SupportQueueScope = 'brands',
) =>
  useQuery({
    // `scope` is IN the key. Without it, switching scope would serve the previous
    // scope's rows from cache and then swap them underneath the reader — which on
    // a queue is worse than a spinner, because the wrong rows look like real ones.
    queryKey: [...INTERNAL_KEY, 'list', status ?? 'all', scope],
    queryFn: () => supportApi.internalList(status, scope),
    retry: false,
  })

/**
 * VRG-721 — the internal sidebar's Support badge: open brand requests.
 *
 * Under INTERNAL_KEY so every status change a staffer makes (which already
 * invalidates INTERNAL_KEY) moves the badge at once; the poll covers requests
 * arriving from OTHER people. 60s, not faster: it runs on every staff page, and a
 * support queue is not a chat — a minute's lag on a new ticket costs nothing.
 * `enabled` is the caller's reachability check, so a role without the queue never
 * makes the call (it would 403 every minute).
 */
export const useInternalSupportOpenCount = (enabled: boolean) =>
  useQuery({
    queryKey: [...INTERNAL_KEY, 'open-count'],
    queryFn: () => supportApi.internalOpenCount(),
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
    retry: false,
  })

export const useInternalSupportRequest = (id: string | undefined) =>
  useQuery({
    queryKey: [...INTERNAL_KEY, 'detail', id],
    queryFn: () => supportApi.internalDetail(id!),
    enabled: Boolean(id),
    retry: false,
  })

/**
 * Reply as Vera, with attachments. The payload matches `useReplyToSupportRequest`'s
 * exactly so `ReplyComposer` — the shared control the brand thread already uses — can be
 * dropped into the internal queue with nothing but an import and a label.
 */
export const useInternalSupportReply = (id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { body: string; attachmentUrls?: string[] | null }) =>
      supportApi.internalReply(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INTERNAL_KEY }),
  })
}

export const useInternalSetSupportStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: SupportRequestStatus }) =>
      supportApi.internalSetStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INTERNAL_KEY }),
  })
}

export const useInternalPromoteRequest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title?: string }) =>
      supportApi.internalPromote(id, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERNAL_KEY })
    },
  })
}

export const useInternalBoardItems = (kind?: 'feature' | 'integration') =>
  useQuery({
    queryKey: [...INTERNAL_KEY, 'board', kind ?? 'all'],
    queryFn: () => supportApi.internalBoard(kind),
    retry: false,
  })

export const useInternalSetBoardStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: BoardItemStatus; note?: string }) =>
      supportApi.internalSetBoardStatus(id, { status, note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERNAL_KEY })
    },
  })
}

/**
 * Create a cross-company theme (VRG-324).
 *
 * Invalidates the internal key only. A theme is invisible to a company caller by
 * construction, and there is no longer a company-facing board query to invalidate
 * anyway — what must re-rank is the internal list, since the new theme's
 * `distinctCompanyCount` changes its ordering.
 */
export const useInternalCreateTheme = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { title: string; description?: string; fromItemId?: string }) =>
      supportApi.internalCreateTheme(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INTERNAL_KEY }),
  })
}

/** Link an item to a theme, or unlink it with `themeItemId: null`. */
export const useInternalSetItemTheme = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, themeItemId }: { id: string; themeItemId: string | null }) =>
      supportApi.internalSetItemTheme(id, themeItemId),
    // The whole list, not the one row: linking changes the TARGET theme's
    // distinct-company count as well as the linked item, and the theme is a
    // different row.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INTERNAL_KEY }),
  })
}

export const useInternalMergeBoardItems = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sourceId, targetItemId }: { sourceId: string; targetItemId: string }) =>
      supportApi.internalMergeBoardItems(sourceId, targetItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERNAL_KEY })
    },
  })
}
